import React, { useCallback, useEffect, useMemo, useRef, useState, use } from "react";
import { Platform, StyleSheet } from "react-native";
import PhoneField, { PhoneFieldHandle } from "../../components/PhoneField";
import { useI18n } from "../../hooks/i18n-hooks";
import { useTheme } from "../../hooks/use-theme";
import VStack from "../../ui_atoms/VStack";
import commonStyle from "../common-style";
import { getAuth, RecaptchaVerifier } from "../../contexts/FirebaseAuth";
import Text from "../../components/Text";
import { v4 as uuidv4 } from "uuid";
import { StackScreenProps } from "@react-navigation/stack";
import Constants from "expo-constants";
import { phoneNumberConfirmResult } from "../../atoms/auth";
import { AuthManagerContext } from "../../contexts/AuthManager";
import ScreenCard from "../ScreenCard";
import { FirebaseAuthTypes } from "@react-native-firebase/auth";
import { SignupSigninStackParamList } from "../../navigations/SignupSigninStack";
import { FirebaseManagerContext } from "../../contexts/FirebaseManager";
import { useSetAtom } from "jotai";
import { captureFirebaseAuthException } from "../../utils";

const CAPTCHA_ID = uuidv4();

const captchaDisabledPhones = (
    (Constants.expoConfig?.extra?.captchaDisabledPhones as string) || ""
).split(",");

type MessageInfo = {
    text: string;
    color?: string;
};

type PhoneNumberScreenProps = StackScreenProps<SignupSigninStackParamList, "PhoneNumber">;

const PhoneNumberScreen = ({ navigation, route }: PhoneNumberScreenProps) => {
    const { isSignup = false } = route.params || {};
    const styles = useStyles();
    const i18n = useI18n();
    const { signOut } = use(FirebaseManagerContext);
    const phoneInputRef = useRef<PhoneFieldHandle>(null);
    const [message, setMessage] = useState<MessageInfo>();
    const [isProcessing, setIsProcessing] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState("");
    const setPhoneNumberConfirmResult = useSetAtom(phoneNumberConfirmResult);
    const { user, signInWithPhoneNumber } = use(AuthManagerContext);
    const isNextButtonDisabled = user !== undefined;

    const [isValid, setIsValid] = useState(false);
    useEffect(() => {
        setIsValid(
            (phoneNumber?.length || 0) > 0 && !!phoneInputRef.current?.isValidNumber(phoneNumber),
        );
    }, [phoneNumber]);

    const onPhoneNumberChanged = useCallback((text: string) => {
        setPhoneNumber(text);
        setMessage(undefined);
    }, []);

    const validate = useCallback(async () => {
        try {
            setIsProcessing(true);
            const phoneValid = phoneInputRef.current?.isValidNumber(phoneNumber);
            if (!phoneValid) {
                setMessage({
                    text: i18n.t("screens.auth.phone_number.invalid_number"),
                    color: "red",
                });
                return;
            }

            const formattedPhoneNumber = phoneInputRef.current?.formatPhoneNumber();
            if (Constants.expoConfig?.extra?.appEnv === "local") {
                // On local environment, we disable app verification for testing
                // But we need to set the phone number we want to use as test number in firebase
                getAuth().settings.appVerificationDisabledForTesting = false;
            }
            if (formattedPhoneNumber) {
                if (Platform.OS === "web") {
                    if (captchaDisabledPhones.includes(formattedPhoneNumber)) {
                        getAuth().settings.appVerificationDisabledForTesting = true;
                    }

                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    if (!window.verifier) {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        window.verifier = new RecaptchaVerifier(getAuth(), CAPTCHA_ID, {
                            size: "invisible",
                        });
                    }
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                    window.verifier.verify();
                }

                const confirmation = await signInWithPhoneNumber(phoneNumber);
                setPhoneNumberConfirmResult(confirmation);
                navigation.navigate("ConfirmCode", {
                    phoneNumber: formattedPhoneNumber,
                    isSignup,
                });
                // The verifier is not destroyed here: Firebase already resets it after each send.
                // `clear()` does not remove an invisible captcha widget from the DOM, so
                // recreating one afterwards would fail with "reCAPTCHA has already been rendered
                // in this element". Cleanup happens when the screen unmounts.
            }
        } catch (err) {
            captureFirebaseAuthException(err);
            let errorMessage: string;
            if (err instanceof Error) {
                // eslint-disable-next-line no-console
                console.log("Error: ", err);
                switch ((err as Partial<FirebaseAuthTypes.PhoneAuthError>).code) {
                    case "auth/invalid-phone-number":
                        errorMessage = i18n.t("screens.auth.phone_number.invalid_number");
                        break;
                    case "auth/missing-phone-number":
                        errorMessage = i18n.t("screens.auth.phone_number.missing_phone_number");
                        break;
                    case "auth/quota-exceeded":
                        errorMessage = i18n.t("screens.auth.phone_number.quota_exceeded");
                        break;
                    case "auth/too-many-requests":
                        errorMessage = i18n.t("screens.auth.phone_number.too_many_requests");
                        break;
                    case "auth/user-disabled":
                        errorMessage = i18n.t("screens.auth.phone_number.user_disabled");
                        break;
                    case "auth/operation-not-allowed":
                        errorMessage = i18n.t("screens.auth.phone_number.operation_not_allowed");
                        break;
                    default:
                        errorMessage = i18n.t("errors.unknown_error");
                        break;
                }
            } else {
                errorMessage = String(err);
            }
            setMessage({ text: errorMessage, color: "red" });
        } finally {
            setIsProcessing(false);
        }
    }, [
        phoneNumber,
        i18n,
        signInWithPhoneNumber,
        setPhoneNumberConfirmResult,
        navigation,
        isSignup,
    ]);

    useEffect(() => {
        const unsubscribe = navigation.addListener("focus", () => {
            try {
                void signOut();
            } catch (error) {
                console.error("Error signing out on focus:", error);
            }
        });
        return unsubscribe;
    }, [navigation, signOut]);

    useEffect(() => {
        return () => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (window.verifier) {
                try {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                    window.verifier.clear();
                } catch {
                    // Ignore cleanup errors
                }
                // `clear()` only marks the verifier as destroyed, so the global must be released
                // too: otherwise the next mount reuses a destroyed verifier and Firebase fails
                // with auth/internal-error.
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                window.verifier = null;
            }
        };
    }, []);

    useEffect(() => {
        if (Platform.OS === "web") {
            // autoFocus prop doesn't work on react-native-phone-number-input on web
            document.getElementById("phone-number-input")?.focus?.();
        }
    }, []);
    return (
        <>
            <ScreenCard
                buttons={[
                    {
                        title: i18n.t("screens.auth.phone_number.next_button_title"),
                        isValid: isValid && !isNextButtonDisabled,
                        validating: isProcessing,
                        onPress: validate,
                    },
                ]}
            >
                <VStack gap={1} v={1} h="2/3">
                    <PhoneField
                        testID="phone-number-input"
                        ref={phoneInputRef}
                        value={phoneNumber}
                        onPhoneNumberChanged={onPhoneNumberChanged}
                        autoFocus
                        label={i18n.t("screens.auth.phone_number.phone_placeholder")}
                        isValid={isValid}
                        style={{ width: "100%" }}
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                        onSubmitEditing={validate}
                    />
                    <Text style={styles.text}>
                        {i18n.t(
                            isSignup
                                ? "screens.auth.phone_number.description_signup"
                                : "screens.auth.phone_number.description",
                        )}
                    </Text>
                    {message && (
                        <Text style={[commonStyle.screenErrorMessage, { color: message.color }]}>
                            {message.text}
                        </Text>
                    )}
                </VStack>
            </ScreenCard>
            {Platform.OS === "web" && <div id={CAPTCHA_ID} />}
        </>
    );
};

const useStyles = () => {
    const { fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                text: {
                    ...fonts.bodyMedium,
                },
                textWeb: {
                    ...fonts.headlineMedium,
                    textAlign: "center",
                },
            }),
        [fonts.bodyMedium, fonts.headlineMedium],
    );
};

export default PhoneNumberScreen;
